import { useEffect, useState } from 'react'
import { Button } from '../../shared/components/Button'
import RadioGroup from '../../shared/components/RadioGroup'
import { FormInput } from '../../shared/components/Fieldset'
import { useCurrency } from '../../util/CurrencyContext'
import { useTranslation } from 'react-i18next'
import ZakahCalculator from './ZakahCalculator'
import { FaArrowLeft } from 'react-icons/fa'
import CurrencyFormat from './CurrencyFormat'

export default function DonationCard({ donationData, onAmountChange }) {
    const [customAmount, setCustomAmount] = useState('')
    const [selectedAmount, setSelectedAmount] = useState(null)
    const [amountType, setAmountType] = useState(null) // 'predefined', 'custom', or 'zakah'
    const [donationID, setDonationID] = useState(null) // null for custom or zakah
    const [showWarning, setShowWarning] = useState(false)
    const [selectedFrequency, setSelectedFrequency] = useState('single')
    const [zakahAmount, setZakahAmount] = useState(null)
    const [convertedZakahAmount, setConvertedZakahAmount] = useState(null)
    const [selectedOptionId, setSelectedOptionId] = useState(null)

    const { t } = useTranslation()

    const { currency, currencySymbol, currencyRate } = useCurrency()

    const {
        donation_options = [],
        custom_amount_enabled = false,
        frequent_options = 'single',
        call_to_action,
        is_zakat,
        enable_zakat_calculator,
    } = donationData

    const frequencyOptions = frequent_options?.split(',').map(opt => ({
        label: opt === 'single' ? t('donation.frequency.oneTime') : t('donation.frequency.monthly'),
        value: opt,
    }))

    const handleAmountChange = (amount, type = 'predefined', id) => {
        setSelectedAmount(amount)
        setAmountType(type)
        setShowWarning(false) // clear warning when user selects a value
        setDonationID(id)

        // Clear other amount types when one is selected
        if (type === 'zakah') {
            setCustomAmount('')
            setSelectedOptionId(null)
        } else if (type === 'custom') {
            setZakahAmount(null)
            setConvertedZakahAmount(null)
            setSelectedOptionId(null)
        } else if (type === 'predefined') {
            setCustomAmount('')
            setZakahAmount(null)
            setConvertedZakahAmount(null)
            setSelectedOptionId(id)
        }
    }

    const handleCTAClick = () => {
        let validAmount = null
        let finalAmountType = amountType

        // Determine which amount to use
        if (zakahAmount && zakahAmount > 0) {
            validAmount = parseFloat(zakahAmount)
            finalAmountType = 'zakah'
        } else if (amountType === 'custom' && customAmount) {
            validAmount = parseFloat(customAmount)
        } else if (amountType === 'predefined' && selectedAmount) {
            validAmount = selectedAmount
        }

        if (!validAmount || validAmount <= 0) {
            setShowWarning(true)
            return
        }

        setShowWarning(false)
        onAmountChange &&
            onAmountChange({
                amount: validAmount,
                type: finalAmountType,
                id: donationID,
                frequent_option: selectedFrequency,
            })
    }

    useEffect(() => {
        if (zakahAmount && zakahAmount > 0) {
            const newZakahAmount = zakahAmount * currencyRate
            setConvertedZakahAmount(Number(newZakahAmount).toFixed(2))
        } else {
            setConvertedZakahAmount(null)
        }
    }, [zakahAmount, currencyRate])

    // Helper function to check if an option is selected
    const isOptionSelected = optionId => {
        return selectedOptionId === optionId && amountType === 'predefined'
    }

    // Check if donate button should be enabled
    const isDonateButtonEnabled = () => {
        return (
            (amountType === 'predefined' && selectedAmount) ||
            (amountType === 'custom' && customAmount) ||
            (zakahAmount && zakahAmount > 0)
        )
    }

    return (
        <div className='sticky p-6 my-2 ml-1 transition-all duration-300 border shadow-lg bg-gradient-to-br from-white to-amber-50 border-amber-100 top-20 rounded-2xl h-fit hover:shadow-xl'>
            {/* Header with decorative elements */}
            <div className='relative mb-6 text-center'>
                <h3 className='text-xl font-bold text-gray-900'>{t('donation.chooseAmount')}</h3>
                <div className='w-12 h-1 mx-auto mt-2 rounded-full bg-gradient-to-r from-amber-400 to-yellow-400'></div>
                <div className='absolute w-3 h-3 rounded-full -top-1 -right-1 bg-amber-300'></div>
                <div className='absolute w-2 h-2 bg-yellow-300 rounded-full -bottom-1 -left-1'></div>
            </div>

            {/* Donation Options Grid */}
            <div className='mb-6 space-y-3'>
                {donation_options.map(option => {
                    const isSelected = isOptionSelected(option.id)

                    return (
                        <button
                            key={option.id}
                            onClick={() => {
                                handleAmountChange(option.amount, 'predefined', option.id)
                                setCustomAmount('')
                                setZakahAmount(null)
                            }}
                            className={`w-full p-4 text-left rounded-xl border-2 transition-all duration-300 transform hover:-translate-y-0.5 group ${
                                isSelected
                                    ? 'border-amber-500 bg-gradient-to-r from-amber-50 to-yellow-50 shadow-md scale-[1.02] ring-2 ring-amber-200'
                                    : 'border-amber-100 hover:border-amber-300 hover:bg-white'
                            }`}
                        >
                            <div className='flex items-start justify-between'>
                                <div className='flex-1'>
                                    <div className='flex items-center gap-2 mb-1'>
                                        <div
                                            className={`w-2 h-2 rounded-full transition-all ${
                                                isSelected
                                                    ? 'bg-amber-500 scale-125'
                                                    : 'bg-amber-300 group-hover:bg-amber-400'
                                            }`}
                                        ></div>
                                        <span className='text-lg font-bold text-gray-900'>
                                            <CurrencyFormat amount={option.amount} />
                                        </span>
                                    </div>
                                    {option.label && option.label !== 'null' && (
                                        <p className='text-sm text-gray-600'>
                                            {option.label || option.label_en}
                                        </p>
                                    )}
                                </div>
                                {isSelected && (
                                    <div className='ml-2'>
                                        <div className='flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-br from-amber-400 to-yellow-400'>
                                            <svg
                                                className='w-3 h-3 text-white'
                                                fill='none'
                                                stroke='currentColor'
                                                viewBox='0 0 24 24'
                                            >
                                                <path
                                                    strokeLinecap='round'
                                                    strokeLinejoin='round'
                                                    strokeWidth='3'
                                                    d='M5 13l4 4L19 7'
                                                ></path>
                                            </svg>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </button>
                    )
                })}

                {/* Custom Amount Input */}
                {custom_amount_enabled && (
                    <div className='relative group'>
                        <div className='absolute inset-0 transition-all duration-300 bg-gradient-to-r from-amber-400/0 to-yellow-400/0 group-hover:from-amber-400/5 group-hover:to-yellow-400/5 rounded-xl'></div>
                        <div
                            className={`relative p-4 rounded-xl border-2 transition-all duration-300 ${
                                amountType === 'custom'
                                    ? 'border-amber-500 bg-gradient-to-r from-amber-50 to-yellow-50 shadow-md scale-[1.02] ring-2 ring-amber-200'
                                    : 'border-amber-100 hover:border-amber-300'
                            }`}
                        >
                            <div className='flex items-center gap-2 mb-2'>
                                <div
                                    className={`w-2 h-2 rounded-full ${
                                        amountType === 'custom' ? 'bg-amber-500' : 'bg-gray-400'
                                    }`}
                                ></div>
                                <label className='text-sm font-medium text-gray-700'>
                                    {t('donation.enterAmount')}
                                </label>
                            </div>
                            <div className='relative'>
                                <div className='absolute left-0 pl-3 font-bold text-gray-700 transform -translate-y-1/2 top-1/2'>
                                    {currencySymbol}
                                </div>
                                <input
                                    type='number'
                                    value={customAmount}
                                    placeholder={t('donation.customAmountPlaceholder', {
                                        currency: currency,
                                    })}
                                    onChange={e => {
                                        const value = e.target.value
                                        setCustomAmount(value)
                                        handleAmountChange(value, 'custom', null)
                                        setZakahAmount(null)
                                    }}
                                    className='w-full py-2 pl-12 pr-4 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-transparent'
                                    min='0'
                                    step='0.01'
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Zakat Calculator Section */}
                {(is_zakat === 1 || is_zakat === '1') &&
                    (enable_zakat_calculator === 1 || enable_zakat_calculator === '1') && (
                        <div className='space-y-3'>
                            <ZakahCalculator
                                onResultChange={amount => {
                                    setZakahAmount(amount)
                                    handleAmountChange(amount, 'zakah', null)
                                    setCustomAmount('')
                                }}
                            />

                            {convertedZakahAmount > 0 && (
                                <div
                                    className={`p-4 border rounded-xl transition-all duration-300 ${
                                        zakahAmount && zakahAmount > 0
                                            ? 'border-green-500 bg-gradient-to-r from-green-50 to-emerald-50 shadow-md scale-[1.02] ring-2 ring-green-200'
                                            : 'border-green-300 bg-gradient-to-r from-green-50 to-emerald-50'
                                    }`}
                                >
                                    <div className='flex items-center gap-2 mb-1'>
                                        <div className='w-3 h-3 bg-green-500 rounded-full'></div>
                                        <p className='font-semibold text-green-800'>
                                            {t('religious-dues.zakat.zakahDue')}
                                        </p>
                                    </div>
                                    <div className='flex items-baseline gap-1'>
                                        <span className='text-2xl font-bold text-green-900'>
                                            {currencySymbol}
                                            {Number(convertedZakahAmount).toLocaleString('en-US')}
                                        </span>
                                        <span className='text-sm text-green-700'>{currency}</span>
                                    </div>
                                    <div className='pt-2 mt-2 border-t border-green-200'>
                                        <p className='text-xs text-green-700'>
                                            Your calculated Zakat obligation
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
            </div>

            {/* Validation Warning */}
            {showWarning && (
                <div className='p-3 mb-4 border bg-gradient-to-r from-yellow-50 to-amber-50 border-amber-300 rounded-xl animate-pulse'>
                    <div className='flex items-center gap-2'>
                        <div className='w-3 h-3 rounded-full bg-amber-500 animate-bounce'></div>
                        <p className='font-medium text-amber-800'>{t('donation.amountRequired')}</p>
                    </div>
                </div>
            )}

            {/* Frequency Selector */}
            <div className='mb-6'>
                <div className='flex items-center gap-2 mb-3'>
                    <div className='w-2 h-2 rounded-full bg-amber-400'></div>
                    <span className='text-sm font-medium text-gray-700'>
                        {t('homepage.donation.frequency')}
                    </span>
                </div>
                <div className='flex gap-3'>
                    {frequencyOptions.map(option => (
                        <button
                            key={option.value}
                            onClick={() => setSelectedFrequency(option.value)}
                            className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all duration-300 ${
                                selectedFrequency === option.value
                                    ? 'border-amber-500 bg-gradient-to-r from-amber-50 to-yellow-50 text-gray-900 font-semibold'
                                    : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50 text-gray-700'
                            }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Donate Button */}
            <div className='relative'>
                <div className='absolute transition-opacity duration-500 opacity-0 -inset-1 bg-gradient-to-r from-amber-400 to-yellow-400 rounded-2xl blur group-hover:opacity-20'></div>
                <button
                    onClick={handleCTAClick}
                    disabled={!isDonateButtonEnabled()}
                    className={`relative w-full py-4 px-6 rounded-xl text-lg font-bold transition-all duration-500 transform hover:scale-[1.02] ${
                        isDonateButtonEnabled()
                            ? 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white shadow-lg hover:shadow-xl'
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                >
                    <div className='flex items-center justify-center gap-2'>
                        <span>{call_to_action || t('donation.donateNow')}</span>
                    </div>

                    {/* Particle effect when enabled */}
                    {isDonateButtonEnabled() && (
                        <>
                            <div className='absolute top-2 right-4 w-1.5 h-1.5 bg-white rounded-full animate-ping'></div>
                            <div className='absolute w-1 h-1 delay-200 rounded-full bottom-2 left-4 bg-white/80 animate-pulse'></div>
                        </>
                    )}
                </button>
            </div>

            {/* Security/Trust Badge */}
            <div className='pt-4 mt-6 border-t border-gray-100'>
                <div className='flex items-center justify-center gap-2 text-xs text-gray-600'>
                    <svg className='w-4 h-4 text-green-500' fill='currentColor' viewBox='0 0 20 20'>
                        <path
                            fillRule='evenodd'
                            d='M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                            clipRule='evenodd'
                        />
                    </svg>
                    <span>{t('donation.zakah.secure')}</span>
                </div>
            </div>
        </div>
    )
}
